/**
 * This browser's device signing key (docs/signing.md §4).
 *
 * ECDSA P-256, generated with `extractable: false` and kept in IndexedDB — structured clone
 * keeps the private half non-extractable. One passkey tap endorses it; the endorsement lasts
 * 90 days and is renewed when fewer than 14 remain.
 *
 * "Just this session" (lib/session-only.ts): the same key type, but lib/idb.ts is in memory-only
 * mode, so the key is never written to IndexedDB; its endorsement is `scope: 'session'` (≤ 12 h).
 */
import {
  createDeviceEndorsementChallenge,
  createDeviceEndorsementStatement,
  type DeviceEndorsement,
  type DeviceSigningKey,
  type EcPublicJwk,
  exportP256PublicJwk,
  generateP256KeyPair,
  isSessionEndorsement,
  jwkThumbprint,
} from '@viko/crypto'
import { clock } from './clock.js'
import { idb } from './idb.js'
import type { PasskeyProvider } from './passkeys.js'

const KEY = 'device-key/v1'
export const RENEW_BEFORE_MS = 14 * 86_400_000

export interface StoredDevice extends DeviceSigningKey {
  publicKey: CryptoKey
  publicJwk: EcPublicJwk
  label: string
  createdAt: number
  endorsement?: DeviceEndorsement
}

let cached: StoredDevice | undefined

export async function loadDevice(): Promise<StoredDevice | undefined> {
  if (cached) return cached
  cached = await idb.get<StoredDevice>(KEY)
  return cached
}

/** Create the key on first use. Never replaces an existing key. */
export async function ensureDeviceKey(label = deviceLabel()): Promise<StoredDevice> {
  const existing = await loadDevice()
  if (existing) return existing
  const pair = await generateP256KeyPair({ extractable: false })
  const publicJwk = await exportP256PublicJwk(pair.publicKey)
  const dev: StoredDevice = {
    deviceKeyId: await jwkThumbprint(publicJwk),
    privateKey: pair.privateKey,
    publicKey: pair.publicKey,
    publicJwk,
    label,
    createdAt: clock.now(),
  }
  await idb.set(KEY, dev)
  cached = dev
  // Best effort: an installed PWA is exempt from ITP eviction; a tab may still lose the key.
  if (!idb.memoryOnly) void navigator.storage?.persist?.().catch(() => {})
  return dev
}

/** `session`: a live "Just this session" endorsement (memory-only key; never renewed). */
export type EndorsementState = 'missing-key' | 'unendorsed' | 'renew' | 'ok' | 'session'

export function endorsementState(
  dev: StoredDevice | undefined,
  now = clock.now(),
): EndorsementState {
  if (!dev) return 'missing-key'
  const e = dev.endorsement
  if (!e || e.statement.expiresAt <= now) return 'unendorsed'
  if (isSessionEndorsement(e.statement)) return 'session'
  if (e.statement.expiresAt - now < RENEW_BEFORE_MS) return 'renew'
  return 'ok'
}

/** Signed in and endorsed (trusted, due for renewal, or for this session): the app may open. */
export function endorsed(state: EndorsementState): boolean {
  return state === 'ok' || state === 'renew' || state === 'session'
}

/** "Mac · Chrome · this session": how a session device is listed and announced. */
export function sessionLabel(label: string): string {
  return /· this session$/.test(label) ? label : `${label} · this session`
}

/**
 * One passkey tap endorses the device key. The endorsement is uploaded so the hub can relay it
 * to every daemon (and it rides inline on commands until a daemon has seen it).
 */
export async function endorseDevice(
  passkeys: PasskeyProvider,
  upload: (e: DeviceEndorsement) => Promise<unknown>,
  opts: { label?: string; rpId?: string; allowCredentials?: string[]; session?: boolean } = {},
): Promise<StoredDevice> {
  if (opts.session && !idb.memoryOnly)
    throw new Error('A session-only key must be created in memory-only mode')
  // A trusted endorsement of a memory-only key would vanish with the tab (session-only.ts).
  if (!opts.session && idb.memoryOnly)
    throw new Error('Leave “Just this session” before trusting this device')
  const dev = await ensureDeviceKey(opts.label)
  const label = opts.label ?? dev.label
  const statement = await createDeviceEndorsementStatement({
    publicKey: dev.publicJwk,
    label: opts.session ? sessionLabel(label) : label,
    now: clock.now(),
    ...(opts.session ? { session: true } : {}),
  })
  const assertion = await passkeys.assert(await createDeviceEndorsementChallenge(statement), {
    ...(opts.rpId ? { rpId: opts.rpId } : {}),
    ...(opts.allowCredentials?.length ? { allowCredentials: opts.allowCredentials } : {}),
  })
  const endorsement: DeviceEndorsement = { statement, assertion }
  await upload(endorsement)
  const next = { ...dev, label: statement.label, endorsement }
  await idb.set(KEY, next)
  cached = next
  return next
}

/** Store an endorsement produced elsewhere (single-ceremony sign-in) for this device key. */
export async function saveEndorsement(
  dev: StoredDevice,
  endorsement: DeviceEndorsement,
): Promise<StoredDevice> {
  if (endorsement.statement.deviceKeyId !== dev.deviceKeyId)
    throw new Error('endorsement is for another device key')
  if (idb.memoryOnly && !isSessionEndorsement(endorsement.statement))
    throw new Error('Leave “Just this session” before trusting this device')
  const next = { ...dev, label: endorsement.statement.label, endorsement }
  await idb.set(KEY, next)
  cached = next
  return next
}

export async function forgetDevice() {
  cached = undefined
  await idb.del(KEY)
}

export function _resetDeviceCache() {
  cached = undefined
}

/** "iPhone · Safari", "Mac · Chrome (app)" — shown in the devices list and daemon notifications. */
export function deviceLabel(ua = globalThis.navigator?.userAgent ?? ''): string {
  const os = /iPhone/.test(ua)
    ? 'iPhone'
    : /iPad/.test(ua)
      ? 'iPad'
      : /Android/.test(ua)
        ? 'Android'
        : /Mac OS X|Macintosh/.test(ua)
          ? 'Mac'
          : /Windows/.test(ua)
            ? 'Windows'
            : /Linux/.test(ua)
              ? 'Linux'
              : 'Browser'
  const browser = /Edg\//.test(ua)
    ? 'Edge'
    : /Firefox\//.test(ua)
      ? 'Firefox'
      : /Chrome\/|CriOS\//.test(ua)
        ? 'Chrome'
        : /Safari\//.test(ua)
          ? 'Safari'
          : 'Browser'
  const standalone =
    typeof matchMedia === 'function' && matchMedia('(display-mode: standalone)').matches
  return `${os} · ${browser}${standalone ? ' (app)' : ''}`
}
